import {
    TaxDocumentAttachmentDto,
    TaxDocumentAttachmentResult,
    TaxDocumentDto,
    TaxDocumentItemRequest,
    TaxDocumentRequest,
    TaxDocumentUploadRequest,
} from "../../../backend/taxguru";
import { toastPromiseWrap } from "../../../utils/notificationUtils";
import {
    approveDocument,
    changeDocumentState,
    reRunRecogniserTask,
    confirmDocument,
    createByAttachments,
    deleteDocument,
    deleteExportTask,
    disapproveDocument,
    documentBatchRequest,
    downloadISDOC,
    saveExtractedDocument,
} from "../../../backend/taxguru/client/taxDocumentClient";
import i18n from "../../../i18n";
import { BatchOperationRequest, CoreAttachmentDto, CoreUser, FilterField, isValueDefined, OrderBy } from "@mm-products/java-multima-framework";
import { UploadMode } from "../upload/useDocumentUpload";
import { listDocumentAttachments } from "../../../backend/taxguru/client/taxDocumentAttachmentClient";
import { RecogniserResponse } from "../../../backend/taxguru/models/RecogniserResponse";
import { Path, PathValue } from "react-hook-form";
import { downloadAttachment } from "../../../backend/taxguru/client/jmf/coreAttachmentClient";
import { toast } from "react-toastify";
import { DocumentDetailFormTypes } from "../detail/DocumentDetail.types";
import { parseFileNameFromContentDisposition } from "../../../utils/axiosUtils";
import { dateForTaxLocalDate } from "../../../utils/dateObjectUtils";
import { isDocNameRegex } from "./documentUtils";
import {
    createDocumentItem,
    deleteDocumentItem,
    splitDocumentItemByBase,
    updateDocumentItem,
} from "../../../backend/taxguru/client/taxDocumentItemsClient";
import { AttachmentContent } from "../detail/section/DocumentAttachmentCarousel";
import { toastStyles } from "../../../TaxGuruStyles";
import { DocumentStateAction } from "./hook/useDocumentStateUpdate";
import { DocumentItemSplitByTaxCentreFormDialogProps } from "../items/DocumentItemSplitByCentreFormDialog";
import source = TaxDocumentUploadRequest.source;
import operator = FilterField.operator;
import sort = OrderBy.sort;

export const orderByCompatibleWithDocumentPagination = (operator = sort.ASC): Array<OrderBy> => [
    { field: "created", sort: operator },
    { field: "id", sort: operator },
];

export const saveExtractedDocumentWrap = (documentId: string, taxDocument: TaxDocumentRequest): Promise<TaxDocumentDto> => {
    return toastPromiseWrap(
        {
            pending: i18n.t("document.result.documentUpdatePending").toString(),
            success: i18n.t("document.result.documentUpdated").toString(),
        },
        (toastId) => saveExtractedDocument(documentId, taxDocument, toastId)
    ).then(({ data }) => {
        console.debug("Document saved", data);
        return data;
    });
};

export const buttonChangeDocumentStateWrap = (documentId: string, action: DocumentStateAction, approver?: CoreUser, comment?: string) => {
    return toastPromiseWrap(
        {
            pending: i18n
                .t("document.result.documentStateChangePending", {
                    status: i18n.t(`document.document-state.${action === "APPROVE" ? "CONFIRMED" : "DO_NOT_PROCESS"}`),
                })
                .toString(),    },
        (toastId) => {
            if (action === "APPROVE") {
                return approveDocument(documentId, comment, undefined, toastId);
            }
            else {
                return disapproveDocument(documentId, comment, undefined, toastId);
            }
        }
    );
};

export const changeDocumentStateWrap = (documentId: string, status?: TaxDocumentDto.status, comment?: string) => {
    return toastPromiseWrap(
        {
            pending: i18n
                .t("document.result.documentStateChangePending", {
                    status: i18n.t(`document.document-state.${status}`),
                })
                .toString(),
            success: i18n.t("document.result.documentUpdated").toString(),
        },
        (toastId) => changeDocumentState(documentId, status, comment, undefined, toastId)
    );
};

export const confirmDocumentWrap = (documentId: string, taxDocument: TaxDocumentRequest): Promise<TaxDocumentDto> => {
    return toastPromiseWrap(
        {
            pending: i18n.t("document.result.documentUpdatePending").toString(),
            success: i18n.t("document.result.documentUpdated").toString(),
        },
        (toastId) => confirmDocument(documentId, taxDocument, toastId)
    ).then(({ data }) => {
        console.debug("Document confirmed", data);
        return data;
    });
};

export function downloadAttachmentForCanvasIfEmpty(
    order: number,
    attachments: Array<TaxDocumentAttachmentDto>,
    images: Array<AttachmentContent>
): Promise<AttachmentContent> {
    if (order < 0 || attachments.length <= order) {
        return Promise.reject("Bad attachments order");
    }
    const attachment = attachments[order].attachment;
    const attachmentId = attachment?.id;
    const attachmentIsIsDoc = attachment?.name ? isDocNameRegex.test(attachment.name) : false;
    const alreadyFetched = images.some((img) => img.attachmentId === attachmentId);
    if (!attachmentId || alreadyFetched || attachmentIsIsDoc) {
        return Promise.reject("Attachment is inappropriate for canvas or is already loaded");
    }
    return downloadAttachment(attachmentId, undefined, true)
        .then((res) => ({
            attachmentId,
            blobUrl: URL.createObjectURL(res.data),
        }))
        .catch((reason) => {
            toast.error(i18n.t("document.result.attachmentNotFound").toString(), {
                toastId: "document.result.attachmentNotFound",
                style: toastStyles.error,
            });
            throw reason;
        });
}

export function createDocumentsByMode(clientId: string, attachments: Array<CoreAttachmentDto>, uploadMode: UploadMode) {
    if (uploadMode === "single-document") {
        const request: TaxDocumentUploadRequest = {
            clientId: clientId,
            attachments: attachments.map((attachment, idx) => ({
                attachmentId: attachment?.id,
                order: idx,
            })),
            source: source.WEB_FILE,
        };
        return toastPromiseWrap(
            {
                pending: i18n.t("document.result.documentCreationPending").toString(),
                success: i18n.t("document.result.documentCreated").toString(),
            },
            (toastId) => createByAttachments(request, toastId)
        );
    } else {
        const attachmentIds = attachments.map((attachment) => ({ attachmentId: attachment.id }));
        const request: TaxDocumentUploadRequest = {
            clientId: clientId,
            attachments: attachmentIds,
            source: source.WEB_FILE,
            multi: true,
        };
        return toastPromiseWrap(
            {
                pending: i18n.t("document.result.documentsCreationPending", { n: attachments.length }).toString(),
                success: i18n.t("document.result.documentsCreated", { n: attachments.length }).toString(),
            },
            (toastId) => createByAttachments(request, toastId)
        );
    }
}

export function fetchSortedAttachments(documentId: string, attachmentType?: TaxDocumentAttachmentDto.type): Promise<TaxDocumentAttachmentResult> {
    return listDocumentAttachments({
        filters: [
            {
                field: "document",
                operator: operator.EQ,
                value: documentId,
            },
        ],
        ...(attachmentType && {
            specificFilters: [
                {
                    name: "attachmentType",
                    value: attachmentType,
                },
            ],
        }),
        orderBy: [
            {
                field: "order",
                sort: sort.ASC,
            },
        ],
    }).then((data) => data.data);
}

/**
 * @param document
 * @param correction no correction is sent if undefined
 * @param formValues
 */
export function createDocumentRequest(
    document: TaxDocumentDto,
    correction: RecogniserResponse | undefined,
    formValues: DocumentDetailFormTypes
): TaxDocumentRequest {
    const request: TaxDocumentRequest = {
        ...(correction && { recogniserResponse: correction }),
    };
    const values: Array<{
        name: keyof TaxDocumentRequest;
        value: PathValue<DocumentDetailFormTypes, Path<DocumentDetailFormTypes>>;
    }> = [
        { name: "processorId", value: document.processor?.id },
        { name: "processorNote", value: formValues.processorNote },
        { name: "payableAmount", value: formValues.payableAmount },
        { name: "currencyId", value: formValues.currency?.id },
        { name: "paymentMeans", value: formValues.paymentMeans },
        { name: "customerCompanyIdNumber", value: formValues.customerCompanyIdNumber },
        { name: "supplierCompanyIdNumber", value: formValues.supplierCompanyIdNumber },
        { name: "supplierTaxIdNumber", value: formValues.supplierTaxIdNumber },
        { name: "description", value: formValues.description },
        { name: "dueDate", value: dateForTaxLocalDate(formValues.dueDate) },
        { name: "issueDate", value: dateForTaxLocalDate(formValues.issueDate) },
        { name: "taxableSupplyDate", value: dateForTaxLocalDate(formValues.taxableSupplyDate) },
        { name: "vatApplicationDate", value: dateForTaxLocalDate(formValues.vatApplicationDate) },
        { name: "kind", value: formValues.kind },
        { name: "ownerId", value: formValues.owner?.id },
        { name: "ownerNote", value: formValues.ownerNote },
        { name: "regNumber", value: formValues.regNumber },
        { name: "supplierId", value: formValues.supplier?.id },
        { name: "supplierPersonId", value: formValues.supplierPerson?.id },
        { name: "preAccountingId", value: formValues.preAccounting?.id },
        { name: "type", value: formValues.type },
        { name: "correctorId", value: document.corrector?.id },
        { name: "classification", value: formValues.classification },
        { name: "abraDocumentTypeId", value: formValues.abraDocumentType?.id },
        { name: "centreId", value: formValues.centre?.id },
        { name: "recogniserSupplierName", value: formValues.recogniserSupplierName },
        { name: "recogniserSupplierName", value: formValues.recogniserSupplierName },
        { name: "recogniserSupplierAddress", value: formValues.recogniserSupplierAddress },
        { name: "reverseCharge", value: formValues.reverseCharge },
        { name: "vatApplicable", value: formValues.vatApplicable },
        { name: "variableSymbol", value: formValues.variableSymbol },
        { name: "accountNumber", value: formValues.accountNumber },
        { name: "bankCode", value: formValues.bankCode },
        { name: "iban", value: formValues.iban },
        { name: "bic", value: formValues.bic },
        { name: "activityId", value: formValues.activity?.id },
        { name: "contractId", value: formValues.contract?.id },
    ];
    values.forEach((value) => {
        if (isValueDefined(value.value) && (value.value || value.value === 0)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            request[value.name] = value.value;
        } else {
            delete request[value.name];
        }
    });
    return request;
}

export const downloadISDOCToDownloads = async (taxDocumentId: string) => {
    const response = await downloadISDOC(taxDocumentId);
    const blob = response.data;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = parseFileNameFromContentDisposition(response) ?? "unknown-document-name.isdoc";
    a.click();
    window.URL.revokeObjectURL(blobUrl);
};

export const handleDeleteDocumentFn = (id: string) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("document.result.documentDeletionPending").toString(),
            success: i18n.t("document.result.documentDeleted").toString(),
        },
        (toastId) => deleteDocument(id, toastId)
    ).then(({ data }) => {
        console.debug("Document deleted %s", data);
        return data;
    });
};

export const handleDeleteDocumentItemFn = (documentItemId: string) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("documentItem.result.documentItemDeletionPending").toString(),
            success: i18n.t("documentItem.result.documentItemDeleted").toString(),
        },
        (toastId) => deleteDocumentItem(documentItemId, toastId)
    ).then(({ data }) => {
        console.debug("Document item deleted %s", data);
        return data;
    });
};

export const handleUpdateDocumentItemFn = (documentItemId: string, req: TaxDocumentItemRequest) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("documentItem.result.documentItemUpdatePending").toString(),
            success: i18n.t("documentItem.result.documentItemUpdated").toString(),
        },
        (toastId) => updateDocumentItem(documentItemId, req, toastId)
    ).then(({ data }) => {
        console.debug("Document item updated %s", data);
        return data;
    });
};
export const handleCreateDocumentItem = (req: TaxDocumentItemRequest) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("documentItem.result.documentItemCreatePending").toString(),
            success: i18n.t("documentItem.result.documentItemCreated").toString(),
        },
        (toastId) => createDocumentItem(req, toastId)
    ).then(({ data }) => {
        console.debug("Document item created %s", data);
        return data;
    });
};

export const handleSplitDocumentItemByBase = (payload: DocumentItemSplitByTaxCentreFormDialogProps) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("documentItem.result.documentItemSplitByBasePending").toString(),
            success: i18n.t("documentItem.result.documentItemSplittedByBase").toString(),
        },
        (toastId) => splitDocumentItemByBase(payload, toastId)
    ).then(({ data }) => {
        console.debug("Document item created and updated %s", data);
        return data;
    });
};

export const deleteExportFn = (taskId: string) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("document.result.documentExportDeletionPending").toString(),
            success: i18n.t("document.result.documentExportDeleted").toString(),
        },
        (toastId) => deleteExportTask(taskId, toastId)
    ).then(({ data }) => {
        console.debug("Export deleted %s", data);
    });
};

export const handleBatchDocumentOperation = (request: BatchOperationRequest, count: number) =>
    toastPromiseWrap(
        {
            pending: i18n.t("common.batch.batchOperationCreating", { count }).toString(),
            success: i18n.t("common.batch.batchOperationPlanned", { count }).toString(),
        },
        (toastId) => documentBatchRequest(request, toastId)
    ).then((data) => {
        console.debug("Batch planned %s", data);
        return data;
    });

export const reRunRecogniserTaskWrap = (documentId: string) => {
    return toastPromiseWrap(
        {
            pending: i18n.t("document.result.reRunRecogniserTaskPending").toString(),
            success: i18n.t("document.result.reRunRecogniserTaskSuccess").toString(),
        },
        (toastId) => reRunRecogniserTask(documentId, toastId)
    );
};
